/* eslint-disable react/prop-types */
import React, { useMemo, useRef, useState } from "react";
import { Typography, Button as AntdButton, Spin, Select } from "antd";
import axios from "axios";
import { AutoComplete, Form, Input, Tag } from "antd";
import debounce from "lodash/debounce";
import { ToastContextDispatch } from "Context/CommonContext";
import styled from "styled-components";
import { getAPIRequest } from "utilFunctions/Axios/AxiosRequestConfiguration";

const CustomTag = styled(Tag)`
  border-radius: 6px;
  cursor: default;
  transition: font-size 0.3s, line-height 0.3s, height 0.3s;
  margin-inline-end: 4px;
  padding-inline-start: 8px;
  padding-inline-end: 4px;
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid transparent;
  height: 32px;
  line-height: 30px;
  font-size: 16px;
`;
const Button = ({
  loading = false,
  type = "default",
  shape = "default",
  disabled = false,
  size,
  icon,
  onClick,
  children,
  ...rest
}) => {
  return (
    <AntdButton
      type={type}
      icon={icon}
      loading={loading}
      onClick={onClick}
      size={size}
      disabled={disabled}
      shape={shape}
      {...rest}
    >
      {children}
    </AntdButton>
  );
};
const CustomButton = ({
  loading = false,
  type = "default",
  shape = "default",
  disabled = false,
  size,
  icon,
  onClick,
  children,
  ...rest
}) => {
  return (
    <AntdButton
      type={type}
      icon={icon}
      loading={loading}
      onClick={onClick}
      size={size}
      disabled={disabled}
      shape={shape}
      {...rest}
    >
      {children}
    </AntdButton>
  );
};

const Title = ({ editable = false, level, children }) => {
  return (
    <Typography.Title
      editable={editable}
      level={level}
      style={{ marginBottom: "0px", color: "#333333" }}
    >
      {children}
    </Typography.Title>
  );
};

const Paragraph = ({
  children,
  onClick,
  editableStrWithSuffixPart,
  ...rest
}) => {
  return (
    <Typography.Paragraph
      ellipsis={{
        suffix: editableStrWithSuffixPart,
      }}
      onClick={onClick}
      {...rest}
      style={{ marginBottom: "0px" }}
    >
      {children}
    </Typography.Paragraph>
  );
};

const Text = ({ type = "", children, editable, ...rest }) => {
  return (
    <Typography.Text editable={editable} type={type} {...rest}>
      {children}
    </Typography.Text>
  );
};

const useAutocompleteUserSearch = (apiEndpoint) => {
  const [options, setOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleSearch = async (value) => {
    try {
      const response = await axios.get(`${apiEndpoint}&email=${value}`);
      const users = response.data.users.map((user) => ({
        value: user.id,
        label: user.email,
      }));
      setOptions(users);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const onSelect = (option) => {
    setSelectedUser(option);
  };

  return { options, selectedUser, handleSearch, onSelect };
};
const StyledFormItem = styled(Form.Item)`
  margin-bottom: 12px !important;
`;
const AutocompleteInput = ({ name, label, apiEndpoint, setUserOptions }) => {
  const [options, setOptions] = React.useState([]);
  const [form] = Form.useForm();
  const ToastDispatch = React.useContext(ToastContextDispatch);
  const debouncedSearch = React.useMemo(
    () =>
      debounce(async (value) => {
        let url = `${apiEndpoint}&email=${value}`;
        getAPIRequest(
          url,
          (data) => {
            if (data["response"]) {
              const users = data["response"].map((user) => ({
                value: user.email,
                label: user.email,
                key: user.id,
              }));
              setOptions(users);
            }
          },
          (data) => {
            ToastDispatch({
              isOpen: true,
              type: "Error",
              message: data["message"],
            });
          }
        );
      }, 500), // Adjust debounce delay as needed
    [apiEndpoint]
  );

  const handleSearch = (value) => {
    debouncedSearch(value);
  };

  const onSelect = (value, option) => {
    form.setFieldsValue({ [name]: value });
    localStorage.setItem("userOptions", JSON.stringify(option));
    setUserOptions(option); // Set form field value
  };

  const onClear = () => {
    localStorage.setItem("userOptions", null);
    setUserOptions(null);
  };
  return (
    <StyledFormItem label={label} name={name}>
      <AutoComplete
        allowClear
        autoFocus
        optionLabelProp="label"
        options={options}
        onSelect={onSelect}
        onSearch={handleSearch}
        onClear={onClear}
        placeholder={"Search user"}
      />
    </StyledFormItem>
  );
};
function DebounceSelect({ fetchOptions, debounceTimeout = 800, ...props }) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState([]);
  const fetchRef = useRef(0);
  console.log(fetchOptions, "fetchOptions");
  const debounceFetcher = useMemo(() => {
    const loadOptions = (value) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);
      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        setOptions(newOptions);
        setFetching(false);
      });
    };
    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);
  console.log(options, "fetchOptions");
  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

// Usage of DebounceSelect

async function fetchUserList(name, apiEndpoint) {
  let url = `${apiEndpoint}&email=${name}`;
  let userdata;
  await getAPIRequest({
    url: url,
    success: (data) => {
      if (data["response"]) {
        const users = data["response"].map((user) => ({
          value: user.email,
          label: user.email,
          key: user.id,
        }));
        userdata = users;
      }
    },
    failure: (error) => {
      console.error(error);
    },
  });

  return userdata;
}
const AutocompleteSelect = ({ name, label, apiEndpoint, setUserOptions }) => {
  const [value, setValue] = useState([]);

  return (
    <StyledFormItem label={label} name={name}>
      <DebounceSelect
        name={name}
        label={label}
        mode="multiple"
        value={value}
        placeholder="Select users"
        fetchOptions={(e) => fetchUserList(e, apiEndpoint)}
        onChange={(newValue) => {
          console.log(newValue, "userOptions");
          setValue(newValue);
          setUserOptions(newValue);
        }}
        style={{
          width: "100%",
        }}
      />
    </StyledFormItem>
  );
};
const ChipInput = ({ value = [], onChange }) => {
  const [inputValue, setInputValue] = useState("");

  const handleClose = (removedTag) => {
    const newTags = value.filter((tag) => tag !== removedTag);
    onChange(newTags);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (!inputValue) {
      return;
    }
    if (!value || value.length === 0) {
      onChange([inputValue]);
    } else if (!value.includes(inputValue)) {
      onChange([...value, inputValue]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleInputConfirm();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        padding: "4px",
        border: "1px solid #d9d9d9",
        borderRadius: "4px",
      }}
    >
      {value &&
        Array.isArray(value) &&
        value.map((tag) => (
          <CustomTag
            key={tag}
            closable
            onClose={() => handleClose(tag)}
            style={{ marginBottom: "0px" }}
          >
            {tag}
          </CustomTag>
        ))}
      <Input
        type="text"
        style={{
          border: "none",
          outline: "none",
          boxShadow: "none",
          flex: 1,
          minWidth: "100px",
        }}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputConfirm}
        placeholder="Type and press enter"
      />
    </div>
  );
};

export {
  Button,
  CustomButton,
  Title,
  Paragraph,
  Text,
  useAutocompleteUserSearch,
  AutocompleteInput,
  AutocompleteSelect,
  ChipInput,
};
